import React, {
  FunctionComponent,
  Reducer,
  useReducer,
  createContext,
  useEffect,
  useState,
} from "react";
import {
  Action,
  ActionType,
  AppContextType,
  AppState,
  PharmacyType,
  PrescriptionDrug,
  UserLanguage,
  LowIncomeSubsidyStatus,
  PlanSNPType,
  EventTrackingPayload,
  IRAInsulinCoachmarkType,
  OecStatuses,
  Beneficiary,
} from "../../@types";
import * as TypeGuards from "../../@types/guards";
import {
  getLoggedInBeneInfo,
  saveBeneDrugs,
  saveBenePharmacies,
} from "../../api";
import { hasLocalSession } from "../../helpers/loginHelpers";
// Do not attempt to import from re-exports (../../helpers) at the moment
// This breaks tests
import { storageKey, cachedStore } from "../../helpers/storeHelpers";
import { logError } from "../../helpers";
import cookies from "browser-cookies";
import { deduplicatePrescriptions } from "../../helpers/beneficiaryInfoHelpers";
import { CoverageType } from "../../pages/QuestionRoutingPage/types";
import { useHistory } from "react-router";
import { ApiError } from "../../helpers/errors";
import { useFlags } from "launchdarkly-react-client-sdk";
import { LDFlagSet } from "launchdarkly-js-client-sdk";
import { AnalyticsActions } from "../contexts/Analytics/types";
import { analyticsReducer } from "../contexts/Analytics/reducer";
import isEqual from "lodash.isequal";

export const defaultState: AppState = {
  beneficiary: undefined,
  compareList: [],
  county: undefined,
  csr: undefined,
  currentCoverage: undefined,
  cwCoverage: undefined,
  degradedIntegrations: [],
  demographicInfo: undefined,
  fips: undefined,
  futureLis: undefined,
  iraInsulinCoachmarkHiddenStates: {
    [IRAInsulinCoachmarkType.PLAN_SEARCH_RESULTS]: false,
    [IRAInsulinCoachmarkType.PLAN_COMPARE]: false,
    [IRAInsulinCoachmarkType.DRUG_LIST]: false,
  },
  language: UserLanguage.ENGLISH,
  lis: undefined,
  mbpHandoffFailed: false,
  mbpReferrerUri: "",
  multipleRolloverPlans: false,
  nextYearCoverage: undefined,
  oecStatuses: { currentYear: undefined, nextYear: undefined },
  pharmacies: [],
  pharmacyAddress: undefined,
  pharmacyType: PharmacyType.NONE,
  prescriptions: [],
  routeParams: {},
  searchResultsFilters: {
    drugCoverageFilter: undefined,
    insuranceCarrierFilter: "",
    insuranceCarriers: [],
    planCategoriesFilter: [],
    planCoveragesFilter: [],
    sortBy: undefined,
    starRatingFilter: "",
  },
  snpFilters: [],
  tealiumLoaded: false,
  trackedEvents: {},
  viewMedigap: false,
  zipcode: undefined,
};

const initialState: AppState = cachedStore || defaultState;

initialState.language = navigator.language.split(/[-_]/)[0] as UserLanguage;

const resetState = (): AppState => {
  // Clear session storage relative to domain
  window.sessionStorage.clear();

  // IE 11 shares session ID cookie over multiple windows and tabs, unlike other browsers
  cookies.erase("sid");

  return { ...defaultState };
};

const updatePrescriptions = async (
  prescriptions: PrescriptionDrug[]
): Promise<void> => {
  if (hasLocalSession()) {
    try {
      saveBeneDrugs(prescriptions);
    } catch (e) {
      logError(
        "Failed to save beneficiary drugs (AppStore/updatePrescriptions)",
        e as ApiError
      );
    }
  }
};

const setPrescriptions = (state: AppState, { payload }: Action): AppState => {
  let prescriptions = TypeGuards.hasPrescriptions(payload)
    ? payload
    : state.prescriptions;

  prescriptions = deduplicatePrescriptions(prescriptions);
  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions(prescriptions);

  return { ...state, prescriptions };
};

const addPrescription = (
  state: AppState,
  { payload: prescription }: Action
): AppState => {
  const prescriptions = TypeGuards.isPrescription(prescription)
    ? [...state.prescriptions, prescription]
    : state.prescriptions;

  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions(prescriptions);

  return {
    ...state,
    prescriptions,
  };
};

const editPrescription = (state: AppState, { payload }: Action): AppState => {
  if (TypeGuards.hasIndex(payload) && TypeGuards.hasDrug(payload)) {
    const { index, drug } = payload;

    const prescriptions = state.prescriptions.map((prescription, i) => {
      if (i !== index) {
        return prescription;
      }

      return {
        ...prescription,
        ...drug,
      };
    });

    updatePrescriptions(prescriptions);

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};

const deletePrescription = (state: AppState, { payload }: Action): AppState => {
  if (TypeGuards.hasIndex(payload)) {
    const prescriptions = state.prescriptions.filter(
      (p, i) => i !== payload.index
    );

    updatePrescriptions(prescriptions);

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};

export const hasMailOrderPharmacy = (state: AppState): boolean => {
  return [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(
    state.pharmacyType
  );
};

const addPharmacy = (
  state: AppState,
  { payload: pharmacy }: Action
): AppState => {
  if (TypeGuards.isPharmacy(pharmacy)) {
    const pharmacies = [...state.pharmacies, pharmacy];
    return { ...state, pharmacies };
  }

  return state;
};

const deletePharmacy = (
  state: AppState,
  { payload: npi }: Action
): AppState => {
  if (typeof npi === "string") {
    const pharmacies = state.pharmacies.filter(p => p.npi !== npi);
    return {
      ...state,
      pharmacies,
    };
  }
  return state;
};

const savePharmacies = (state: AppState): AppState => {
  if (hasLocalSession()) {
    const npis = state.pharmacies.map(p => p.npi);
    if (hasMailOrderPharmacy(state)) {
      npis.push("mail-order");
    }
    try {
      saveBenePharmacies(npis);
    } catch (e) {
      logError(
        "Failed to save beneficiary pharmacies (AppStore/savePharmacies)",
        e as ApiError
      );
    }
  }
  return state;
};

const setPharmacies = (state: AppState, { payload }: Action): AppState => {
  const pharmacies = TypeGuards.hasPharmacies(payload)
    ? payload
    : state.pharmacies;

  return { ...state, pharmacies };
};

const setMailOrderPharmacyNetworkStatus = (
  state: AppState,
  { payload }: Action
): AppState => {
  const mailOrderNetworkStatus = TypeGuards.isMailOrderPharmacyStatus(payload)
    ? payload
    : state.mailOrderNetworkStatus;
  return { ...state, mailOrderNetworkStatus };
};

const updateIraInsulinCoachmarkHiddenStates = (
  state: AppState,
  { payload }: Action
): AppState => {
  const iraInsulinCoachmark = TypeGuards.isIRAInsulinCoachmarkType(payload)
    ? payload
    : undefined;
  if (iraInsulinCoachmark) {
    return {
      ...state,
      iraInsulinCoachmarkHiddenStates: {
        ...state.iraInsulinCoachmarkHiddenStates,
        [iraInsulinCoachmark]: true,
      },
    };
  }
  return state;
};

const updateLis = (
  state: AppState,
  { payload: lis }: Action,
  setFutureLis = false
): AppState => {
  if (!TypeGuards.isLIS(lis)) {
    return state;
  }

  const snpFilters = state.snpFilters || [];

  if (
    [
      LowIncomeSubsidyStatus.LIS_LEVEL_1A,
      LowIncomeSubsidyStatus.LIS_LEVEL_2,
      LowIncomeSubsidyStatus.LIS_LEVEL_3,
    ].includes(lis)
  ) {
    if (!Object.values(snpFilters).includes(PlanSNPType.SNP_TYPE_NOT_SNP)) {
      snpFilters.push(PlanSNPType.SNP_TYPE_NOT_SNP);
    }

    if (
      !Object.values(snpFilters).includes(PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE)
    ) {
      snpFilters.push(PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE);
    }
  }

  return setFutureLis
    ? { ...state, futureLis: lis, snpFilters }
    : { ...state, lis, snpFilters };
};

const updatePlanType = (state: AppState, { payload }: Action): AppState => {
  const planType = TypeGuards.isPlanType(payload) ? payload : undefined;

  return {
    ...state,
    planType,
    searchResultsFilters: defaultState.searchResultsFilters,
  };
};

/**
 * Updates the plan-specific network status for a pharmacy. Called whenever
 * `PharmacyNetworkStatus` is used, to prevent flash-of-incorrect-state issues
 * when switching between plans
 */
const updatePharmacyNetworkStatus = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.isPharmacyInfo(payload)) {
    const pharmacies = state.pharmacies.map(p => {
      return p.npi === payload.npi
        ? { ...p, in_network: payload.in_network, preferred: payload.preferred }
        : p;
    });
    return {
      ...state,
      pharmacies,
    };
  }
  return state;
};

const updateSearchResultsFiltersCarriers = (
  state: AppState,
  { payload }: Action
): AppState => {
  const insuranceCarriers = TypeGuards.hasInsuranceCarriers(payload)
    ? payload
    : [];

  return {
    ...state,
    searchResultsFilters: {
      ...state.searchResultsFilters,
      insuranceCarriers,
    },
  };
};

const updateTrackedEvents = (
  state: AppState,
  { payload }: Action
): AppState => {
  const eventTrackingPayload = payload as EventTrackingPayload;
  const eventToRegister = TypeGuards.isRegisteredEvent(
    eventTrackingPayload?.event
  )
    ? eventTrackingPayload.event
    : null;
  if (eventToRegister && typeof eventTrackingPayload?.value !== "undefined") {
    return {
      ...state,
      trackedEvents: {
        ...state.trackedEvents,
        [eventToRegister]: eventTrackingPayload.value,
      },
    };
  }
  return state;
};

const reducer: Reducer<AppState, AnalyticsActions | Action> = (
  state,
  action
) => {
  function newStateIf(
    method: keyof typeof TypeGuards,
    property: keyof AppState
  ): AppState {
    return TypeGuards[method](payload)
      ? { ...state, [property]: payload }
      : state;
  }

  if ("settings" in action) {
    return analyticsReducer(state, action);
  }
  const { payload } = action;

  switch (action.type) {
    case ActionType.ADD_BENEFICIARY:
      return newStateIf("isBeneficiary", "beneficiary");
    case ActionType.ADD_CURRENT_COVERAGE:
      return newStateIf("isSearchResultPlan", "currentCoverage");
    case ActionType.ADD_NEXT_YEAR_COVERAGE:
      return newStateIf("isSearchResultPlan", "nextYearCoverage");
    case ActionType.ADD_CSR:
      return newStateIf("isCSR", "csr");
    case ActionType.ADD_PHARMACY:
      return addPharmacy(state, action);
    case ActionType.ADD_PRESCRIPTION:
      return addPrescription(state, action);
    case ActionType.DELETE_PHARMACY:
      return deletePharmacy(state, action);
    case ActionType.DELETE_PRESCRIPTION:
      return deletePrescription(state, action);
    case ActionType.EDIT_PRESCRIPTION:
      return editPrescription(state, action);
    case ActionType.RESET_PHARMACIES:
      return { ...state, pharmacies: [] };
    case ActionType.RESET_STATE:
      return { ...state, ...resetState(), language: state.language };
    case ActionType.SAVE_PHARMACIES:
      return savePharmacies(state);
    case ActionType.SET_MAIL_ORDER_PHARMACY_NETWORK_STATUS:
      return setMailOrderPharmacyNetworkStatus(state, action);
    case ActionType.SET_PRESCRIPTIONS:
      return setPrescriptions(state, action);
    case ActionType.SET_PHARMACIES: {
      return setPharmacies(state, action);
    }
    case ActionType.SET_TEALIUM_LOADED:
      return { ...state, tealiumLoaded: payload ? true : false };
    case ActionType.EVENT_TRACKED:
      return updateTrackedEvents(state, action);
    case ActionType.UPDATE_COUNTY:
      return newStateIf("isCounty", "county");
    case ActionType.UPDATE_COMPARE_LIST:
      return newStateIf("hasSearchResultPlans", "compareList");
    case ActionType.UPDATE_FIPS:
      return newStateIf("hasFiveDigitString", "fips");
    case ActionType.UPDATE_IRA_INSULIN_COACHMARK_HIDDEN_STATES:
      return updateIraInsulinCoachmarkHiddenStates(state, action);
    case ActionType.UPDATE_LIS:
      return updateLis(state, action);
    case ActionType.UPDATE_FUTURE_LIS:
      return updateLis(state, action, true);
    case ActionType.UPDATE_PHARMACY_TYPE:
      return newStateIf("isPharmacyType", "pharmacyType");
    case ActionType.UPDATE_PLAN_TYPE:
      return updatePlanType(state, action);
    case ActionType.UPDATE_OEC_STATUSES:
      return { ...state, oecStatuses: payload as OecStatuses };
    case ActionType.UPDATE_MBP_HANDOFF_FAILED:
      return newStateIf("isBoolean", "mbpHandoffFailed");
    case ActionType.UPDATE_MULTIPLE_ROLLOVER_PLANS:
      return newStateIf("isBoolean", "multipleRolloverPlans");
    case ActionType.UPDATE_ZIPCODE:
      return newStateIf("hasFiveDigitString", "zipcode");
    case ActionType.UPDATE_VIEW_MEDIGAP:
      return newStateIf("isBoolean", "viewMedigap");
    case ActionType.UPDATE_DEGRADED_INTEGRATIONS:
      return newStateIf("hasStrings", "degradedIntegrations");
    case ActionType.UPDATE_YEAR:
      return newStateIf("hasYear", "year");
    case ActionType.UPDATE_LANGUAGE:
      return newStateIf("isLanguage", "language");
    case ActionType.UPDATE_SELECTED_PLAN:
      return newStateIf("isPlanOrSRP", "selectedPlan");
    case ActionType.UPDATE_LAST_ACTIVITY:
      return newStateIf("isDate", "lastActivity");
    case ActionType.UPDATE_SNP_FILTERS:
      return newStateIf("hasPlanSNPTypes", "snpFilters");
    case ActionType.UPDATE_ROUTE_PARAMS:
      return {
        ...state,
        routeParams: (payload as Record<string, string>) || {},
      };
    case ActionType.UPDATE_SEARCH_RESULTS_FILTERS:
      return newStateIf("hasSearchResultsFilters", "searchResultsFilters");
    case ActionType.UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS:
      return updateSearchResultsFiltersCarriers(state, action);
    case ActionType.UPDATE_PHARMACY_ADDRESS:
      return { ...state, pharmacyAddress: payload as string };
    case ActionType.UPDATE_PHARMACY_NETWORK_STATUS:
      return updatePharmacyNetworkStatus(state, action);
    case ActionType.UPDATE_DEMOGRAPHICS:
      return newStateIf("isDemographicInfo", "demographicInfo");
    case ActionType.UPDATE_CW_COVERAGE:
      return { ...state, cwCoverage: payload as CoverageType };
    case ActionType.UPDATE_MBP_REFERRER_URI:
      return newStateIf("isString", "mbpReferrerUri");
    case ActionType.UPDATE_LD_FLAGS:
      return { ...state, cachedFlags: payload as LDFlagSet };
    default:
      throw new Error(`Reducer missing handler for:
        ACTION: ${action.type}
        STATE: ${JSON.stringify(state, null, 2)}`);
  }
};

export const AppContext = createContext({} as AppContextType);

export const AppStoreProvider: FunctionComponent<{
  initialState?: AppState;
}> = props => {
  const [state, dispatch] = useReducer(
    reducer,
    props.initialState || { ...initialState, tealiumLoaded: false }
  );
  const [rehydrateBene, setRehydrateBene] = useState<
    Pick<Beneficiary, "mbi" | "ssn" | "hicn">
  >({
    mbi: state?.beneficiary?.mbi || "",
    ssn: state?.beneficiary?.ssn || "",
    hicn: state?.beneficiary?.hicn || "",
  });
  const history = useHistory();
  const flags = useFlags();

  useEffect(() => {
    (async () => {
      if (state.beneficiary && !state.csr && rehydrateBene.mbi === "") {
        const { beneficiary } = await getLoggedInBeneInfo();
        const { mbi, hicn, ssn } = beneficiary || {
          mbi: "",
          hicn: "",
          ssn: "",
        };
        setRehydrateBene({
          mbi,
          hicn,
          ssn,
        });
      }
    })();
  }, [state.beneficiary?.mbi]);

  useEffect(() => {
    const { beneficiary, csr } = state;

    const beneToSet = csr
      ? beneficiary
      : {
          ...beneficiary,
          ssn: "",
          hicn: "",
          mbi: "",
        };

    // Every time the state changes, store it in sessionStorage
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify(
        beneficiary
          ? {
              ...state,
              beneficiary: beneToSet,
            }
          : state
      )
    );
  }, [state]);

  const { mbi, ssn, hicn } = rehydrateBene;

  const stateToSet =
    state.beneficiary && !state.csr
      ? {
          ...state,
          beneficiary: { ...state.beneficiary, mbi, ssn, hicn },
        }
      : state;

  useEffect(() => {
    // Need deep equality check to handle memory leak caused by jest-launchdarkly-mock recreating flags object every test run case run
    if (!isEqual(state.cachedFlags, flags)) {
      dispatch({ type: ActionType.UPDATE_LD_FLAGS, payload: flags });
    }
  }, [flags]);

  // For the purposes of testing global session management, we need to be able to
  // set app state within integration tests
  if (window.Cypress) {
    window.store = {
      state: stateToSet,
      dispatch,
    };
    window.router = { history };
  }

  return (
    <AppContext.Provider value={{ state: stateToSet, dispatch }}>
      {props.children}
    </AppContext.Provider>
  );
};

export default {
  AppContext,
  AppStoreProvider,
};
